import { useState, useEffect, useCallback } from 'react';
import DataTable from '../components/DataTable';
import FleetStyleKpiCard from '../components/FleetStyleKpiCard';
import { Battery, HelpCircle, Download, RefreshCw, Pencil } from 'lucide-react';
import { useDemoMode } from '../contexts/DemoModeContext';
import { usePeriod } from '../contexts/PeriodContext';
import { useOptimizationScope } from '../contexts/OptimizationScopeContext';
import { redactVehicleType, redactStationName, redactVehicleId } from '../utils/demoRedaction';
import { parseCSV, CSVRow } from '../utils/csvParser';
import { API_ENDPOINTS } from '../config/api';
import { STATUS_COLORS } from '../config/colors';
import { logger } from '../utils/logger';
import { authenticatedFetch } from '../utils/authenticatedFetch';

// Interface definitions for electrification data
interface FleetSummary {
  run_id: string;
  timestamp: string;
  total_fleet_size: number;
  total_evs: number;
  total_diesel: number;
  total_vehicles_converted: number;
  conversion_type: string;
  conversion_value: number;
  target_stations: string;
  target_vehicle_types: string;
  baseline_co2_emissions: number;
  projected_co2_emissions: number;
  co2_reduction_percentage: number;
  total_vehicle_cost: number;
  total_infrastructure_cost: number;
  total_investment: number;
  electricity_cost_GBP?: number;
  ev_dispatched?: number;
  diesel_dispatched?: number;
  budget: string;
  budget_utilization: string;
}

interface StationDetail {
  station_name: string;
  total_ev_vehicles: number;
  total_diesel_vehicles: number;
  charging_points: number;
  charging_rates: string;
  [key: string]: string | number; // For vehicle type columns
}

interface VehicleTypeDetail {
  vehicle_type: string;
  number_converted: number;
  total_ev: number;
  total_diesel: number;
  battery_capacity: number;
  cost_per_vehicle: number;
  total_cost: number;
  electricity_cost_GBP?: number;
}

interface GenerationSummary {
  generation: number;
  best_fitness: number;
  best_performance: number;
  best_cost: number;
  population_size: number;
  valid_solutions: number;
}

interface ElectrificationMetadata {
  optimization_metadata?: {
    timestamp?: string;
    region?: string;
    data_model_source?: string;
    priority?: string;
    conversion_criteria?: {
      type?: string;
      value?: number;
      target_vehicle_types?: string[];
      target_stations?: string[];
      prioritize_ev_dispatch?: boolean;
    };
    ga_parameters?: {
      population_size?: number;
      max_generations?: number;
      crossover_rate?: number;
      mutation_rate?: number;
      tournament_size?: number;
      performance_threshold?: number;
    };
    fitness_weights?: {
      performance?: number;
      cost?: number;
      environmental?: number;
      charging?: number;
      performance_percentage?: number;
      cost_percentage?: number;
      environmental_percentage?: number;
      charging_percentage?: number;
    };
    budget_constraint?: {
      budget?: number | null;
      budget_formatted?: string | null;
    };
    run_information?: {
      total_generations?: number;
      best_fitness?: number;
      best_performance?: number;
      elapsed_time?: number;
      simulation_duration_hours?: number;
    };
  };
}

// Helper function to get column value (similar to Optimization.tsx)
const getColumnValue = (row: CSVRow, possibleNames: string[]): string => {
  for (const name of possibleNames) {
    if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
      return String(row[name]);
    }
  }
  return '';
};

export default function Electrification() {
  const { isDemoMode } = useDemoMode();
  const { selectedPeriod } = usePeriod();
  
  // Use shared optimization scope context (shared with Optimization tab)
  const {
    selectedVehicles,
    setSelectedVehicles,
    selectedVehicleTypes,
    setSelectedVehicleTypes,
    selectedStations,
    setSelectedStations,
    availableVehicles,
    availableVehicleTypes,
    availableStations,
    addAvailableVehicles,
    addAvailableVehicleTypes,
    addAvailableStations,
  } = useOptimizationScope();
  
  // State for electrification configuration (user inputs)
  const [conversionTarget, setConversionTarget] = useState<'percentage' | 'vehicles' | 'custom'>('percentage');
  const [targetPercentage, setTargetPercentage] = useState<number>(60);
  const [] = useState<number>(27);
  
  const [totalBudget, setTotalBudget] = useState<number>(3200000);
  const [maxUnitCost, setMaxUnitCost] = useState<number>(100000);
  
  const [prioritizeOperationalEfficiency, setPrioritizeOperationalEfficiency] = useState<boolean>(false);
  const [prioritizeEnvironmental, setPrioritizeEnvironmental] = useState<boolean>(false);
  const [prioritizeBalanced, setPrioritizeBalanced] = useState<boolean>(true);
  
  // Default optimization priorities (matches backend)
  const defaultOptimizationPriorities = {
    performance: 40,  // 40% - most important (matches backend default)
    cost: 30,         // 30% - balance cost (matches backend default)
    environmental: 20, // 20% - CO2 reduction (matches backend default)
    charging: 10,      // 10% - charging efficiency (matches backend default)
  };

  // Priority-specific weightings
  const priorityWeightings = {
    operationalEfficiency: {
      performance: 50,
      cost: 35,
      environmental: 5,
      charging: 10
    },
    environmental: {
      performance: 30,
      cost: 20,
      environmental: 40,
      charging: 10
    },
    balanced: {
      performance: 35,
      cost: 25,
      environmental: 20,
      charging: 20
    }
  };
  
  // State for optimization priorities (weights) - defaults to balanced approach
  const [optimizationPriorities, setOptimizationPriorities] = useState({ ...priorityWeightings.balanced });

  // Data model source selection (controls how available lists are populated)
  type DataModelSource = 'auto' | 'simConfig' | 'utilization' | 'electrificationRun';
  const [dataModelSource, setDataModelSource] = useState<DataModelSource>('auto');
  
  // State for timeline
  const [conversionPhases, setConversionPhases] = useState<number>(3);
  const [phaseDurationMonths, setPhaseDurationMonths] = useState<number>(12);
  const [startDate, setStartDate] = useState<string>('2026-01-01');
  
  // State for electrification results from backend
  const [electrificationRuns, setElectrificationRuns] = useState<string[]>([]);
  const [selectedRun, setSelectedRun] = useState<string>('');
  const [fleetSummary, setFleetSummary] = useState<FleetSummary | null>(null);
  const [stationDetails, setStationDetails] = useState<StationDetail[]>([]);
  const [vehicleTypeDetails, setVehicleTypeDetails] = useState<VehicleTypeDetail[]>([]);
  const [generationSummary, setGenerationSummary] = useState<GenerationSummary[]>([]);
  const [electrificationMetadata, setElectrificationMetadata] = useState<ElectrificationMetadata | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for editable EV unit costs
  const [evUnitCosts, setEvUnitCosts] = useState<Record<string, number>>({});
  const [editingEvUnitCost, setEditingEvUnitCost] = useState<{ vehicleType: string } | null>(null);
  
  // State for editable priority percentages
  const [editingPriority, setEditingPriority] = useState<'performance' | 'cost' | 'environmental' | 'charging' | null>(null);
  
  // State for editable budget values
  const [budgetValues, setBudgetValues] = useState<Record<string, number>>({});
  const [editingBudget, setEditingBudget] = useState<string | null>(null);
  
  // Handle EV unit cost edit
  const handleEvUnitCostEdit = (vehicleType: string, newValue: string) => {
    const numValue = parseFloat(newValue.replace(/[£,\s]/g, '')) || 0;
    setEvUnitCosts(prev => ({ ...prev, [vehicleType]: numValue }));
    setEditingEvUnitCost(null);
  };
  
  // Handle budget edit
  const handleBudgetEdit = (metric: string, newValue: string) => {
    // Extract numeric value from string like "£3.20M" or "No budget set"
    const numValue = newValue.includes('M') 
      ? parseFloat(newValue.replace(/[£,\sM]/g, '')) * 1000000
      : parseFloat(newValue.replace(/[£,\s]/g, '')) || 0;
    
    // If editing TOTAL, also update Vehicle Conversion Cost budget
    if (metric === 'TOTAL') {
      setBudgetValues(prev => ({ 
        ...prev, 
        'TOTAL': numValue,
        'Vehicle Conversion Cost': numValue 
      }));
    } else {
      setBudgetValues(prev => ({ ...prev, [metric]: numValue }));
    }
    setEditingBudget(null);
  };
  
  // Handle priority percentage edit
  const handlePriorityEdit = (priority: 'performance' | 'cost' | 'environmental' | 'charging', newValue: string) => {
    const numValue = Math.max(0, Math.min(100, parseInt(newValue.replace(/[%\s]/g, '')) || 0));
    
    if (priority === 'performance') {
      const total = optimizationPriorities.cost + optimizationPriorities.environmental + optimizationPriorities.charging;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setOptimizationPriorities({
          performance: numValue,
          cost: Math.round(optimizationPriorities.cost * scale),
          environmental: Math.round(optimizationPriorities.environmental * scale),
          charging: remaining - Math.round(optimizationPriorities.cost * scale) - Math.round(optimizationPriorities.environmental * scale),
        });
      } else {
        setOptimizationPriorities({ ...optimizationPriorities, performance: numValue });
      }
    } else if (priority === 'cost') {
      const total = optimizationPriorities.performance + optimizationPriorities.environmental + optimizationPriorities.charging;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setOptimizationPriorities({
          cost: numValue,
          performance: Math.round(optimizationPriorities.performance * scale),
          environmental: Math.round(optimizationPriorities.environmental * scale),
          charging: remaining - Math.round(optimizationPriorities.performance * scale) - Math.round(optimizationPriorities.environmental * scale),
        });
      } else {
        setOptimizationPriorities({ ...optimizationPriorities, cost: numValue });
      }
    } else if (priority === 'environmental') {
      const total = optimizationPriorities.performance + optimizationPriorities.cost + optimizationPriorities.charging;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setOptimizationPriorities({
          environmental: numValue,
          performance: Math.round(optimizationPriorities.performance * scale),
          cost: Math.round(optimizationPriorities.cost * scale),
          charging: remaining - Math.round(optimizationPriorities.performance * scale) - Math.round(optimizationPriorities.cost * scale),
        });
      } else {
        setOptimizationPriorities({ ...optimizationPriorities, environmental: numValue });
      }
    } else if (priority === 'charging') {
      const total = optimizationPriorities.performance + optimizationPriorities.cost + optimizationPriorities.environmental;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setOptimizationPriorities({
          charging: numValue,
          performance: Math.round(optimizationPriorities.performance * scale),
          cost: Math.round(optimizationPriorities.cost * scale),
          environmental: remaining - Math.round(optimizationPriorities.performance * scale) - Math.round(optimizationPriorities.cost * scale),
        });
      } else {
        setOptimizationPriorities({ ...optimizationPriorities, charging: numValue });
      }
    }
    setEditingPriority(null);
  };
  
  // Fetch utilization data to populate vehicle/type/station lists (adds to shared context)
  // Load available vehicles/types/stations from sim_env_config.json for ES mode
  useEffect(() => {
    // Run only when using Sim Env Config or Auto mode
    if (dataModelSource !== 'simConfig' && dataModelSource !== 'auto') return;

    const serviceType = 'emergency_services'; // Electrification is ES mode only
    
    let isCancelled = false;
    
    // Helper function to abbreviate station name
    const getStationAbbreviation = (stationName: string): string => {
      // Take first 3 letters of last word (or full name if < 3 chars), uppercase
      const words = stationName.split(' ');
      const lastWord = words[words.length - 1];
      return lastWord.substring(0, 3).toUpperCase();
    };
    
    // Helper function to abbreviate vehicle type
    const getVehicleTypeAbbreviation = (vehicleType: string): string => {
      // Map full vehicle type names to abbreviations
      const abbreviations: { [key: string]: string } = {
        'Pumping Appliance': 'PA',
        'Response Car': 'RC',
        'Large Van': 'LV',
        'Small Van': 'SV',
        'Light 4x4 Tender': 'LT',
        'Rope Rescue': 'RR',
        'Aerial Ladder Platform': 'ALP',
        'Technical Rescue Unit': 'TRU',
        'Water Carrier': 'WC',
        'Other Appliance': 'OA',
      };
      return abbreviations[vehicleType] || vehicleType.split(' ').map((word: string) => word[0].toUpperCase()).join('');
    };
    
    fetch(API_ENDPOINTS.simEnvConfig(serviceType))
      .then((res: Response) => {
        if (isCancelled) return;
        if (!res.ok) {
          if (res.status === 404) {
            logger.debug('sim_env_config.json not found, falling back to utilization data');
            return null;
          }
          throw new Error(`Failed to load sim_env_config.json: ${res.status}`);
        }
        return res.json();
      })
      .then((response: any) => {
        if (isCancelled || !response) return;
        
        // The API returns { success, data, source, region }
        const config = response.data || response;
        
        const types = new Set<string>();
        const stations: string[] = [];
        const vehicles: string[] = [];
        
        // Extract from fire_stations section
        if (config.fire_stations && typeof config.fire_stations === 'object') {
          const stationsData = config.fire_stations.stations || config.fire_stations;
          Object.entries(stationsData).forEach(([stationName, stationData]: [string, any]) => {
            if (stationData && typeof stationData === 'object' && stationData.vehicles) {
              stations.push(stationName);
              const stationAbbrev = getStationAbbreviation(stationName);
              
              // Extract vehicle types and generate vehicle IDs
              // Note: The config uses 'vehicles' which is an object of vehicle types
              Object.entries(stationData.vehicles).forEach(([vehicleType, vehicleCounts]: [string, any]) => {
                if (vehicleCounts && typeof vehicleCounts === 'object') {
                  const diesel = vehicleCounts.diesel || 0;
                  const ev = vehicleCounts.ev || vehicleCounts.electric || 0;
                  const total = diesel + ev;
                  
                  if (total > 0) {
                    types.add(vehicleType);
                    const vehicleTypeAbbrev = getVehicleTypeAbbreviation(vehicleType);
                    
                    // Generate vehicle IDs: STATION_ABBREV-VEHICLE_TYPE_ABBREV-COUNTER
                    for (let i = 1; i <= total; i++) {
                      const vehicleId = `${stationAbbrev}-${vehicleTypeAbbrev}-${String(i).padStart(2, '0')}`;
                      vehicles.push(vehicleId);
                    }
                  }
                }
              });
            }
          });
        }
        
        // Add to shared context
        if (vehicles.length > 0) addAvailableVehicles(Array.from(vehicles));
        if (types.size > 0) addAvailableVehicleTypes(Array.from(types));
        if (stations.length > 0) addAvailableStations(stations);
      })
      .catch(err => {
        if (isCancelled) return;
        logger.debug('Error loading sim_env_config.json, falling back to utilization data:', err.message);
      });
    
    return () => {
      isCancelled = true;
    };
  }, [addAvailableVehicles, addAvailableVehicleTypes, addAvailableStations, dataModelSource]);

  // Extract available vehicles/types/stations from utilization data (fallback if config not available)
  useEffect(() => {
    if (!selectedPeriod) return;
    // Run only when using Historical Utilization mode (NOT in auto mode - sim config takes priority)
    if (dataModelSource !== 'utilization') return;
    
    const fetchUtilizationData = async () => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.vehicleLevelCSV(selectedPeriod));
        if (res.ok) {
          const text = await res.text();
          const data = parseCSV(text);
          
          // Extract unique vehicles
          const vehicles: string[] = [];
          const types: string[] = [];
          const stations: string[] = [];
          
          data.forEach(row => {
            const vehicleId = getColumnValue(row, ['vehicle_id', 'VEHICLE_ID', 'Vehicle ID', 'vehicleId']);
            const vehicleType = getColumnValue(row, ['vehicle_type', 'VEHICLE_TYPE', 'Vehicle Type', 'vehicleType', 'type', 'Type']);
            const station = getColumnValue(row, ['fire_station', 'FIRE_STATION', 'Fire Station', 'fireStation', 'station', 'Station']);
            
            if (vehicleId) vehicles.push(vehicleId);
            if (vehicleType) types.push(vehicleType);
            if (station) {
              // Extract first word from station name
              const stationName = station.split(' ')[0] || station;
              stations.push(stationName);
            }
          });
          
          // Add to shared context (merges with data from other tabs)
          if (vehicles.length > 0) addAvailableVehicles(vehicles);
          if (types.length > 0) addAvailableVehicleTypes(types);
          if (stations.length > 0) addAvailableStations(stations);
        }
      } catch (error) {
        console.error('Error fetching utilization data:', error);
      }
    };
    
    fetchUtilizationData();
  }, [selectedPeriod, addAvailableVehicles, addAvailableVehicleTypes, addAvailableStations, dataModelSource]);
  
  // Also extract available data from electrification station details (fallback data source)
  useEffect(() => {
    // Populate lists from selected electrification run only when requested or in Auto mode
    if ((dataModelSource === 'electrificationRun' || dataModelSource === 'auto') && stationDetails.length > 0) {
      // Extract station names
      const stations = stationDetails.map(s => s.station_name).filter(Boolean);
      if (stations.length > 0) addAvailableStations(stations);
    }
  }, [stationDetails, addAvailableStations, dataModelSource]);
  
  useEffect(() => {
    // Populate vehicle types from selected electrification run only when requested or in Auto mode
    if ((dataModelSource === 'electrificationRun' || dataModelSource === 'auto') && vehicleTypeDetails.length > 0) {
      // Extract vehicle types - format from snake_case to Title Case
      const types = vehicleTypeDetails.map(vt => 
        vt.vehicle_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      ).filter(Boolean);
      if (types.length > 0) addAvailableVehicleTypes(types);
    }
  }, [vehicleTypeDetails, addAvailableVehicleTypes, dataModelSource]);
  
  // Fetch available electrification runs
  useEffect(() => {
    const fetchElectrificationRuns = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.electrificationResults());
        if (res.ok) {
          const data = await res.json();
          setElectrificationRuns(data.results || []);
          // Default to "electrification_1_east_sussex" if available, otherwise use latest
          const defaultRun = data.results?.find((run: string) => run === 'electrification_1_east_sussex') || data.latest;
          if (defaultRun) {
            setSelectedRun(defaultRun);
          }
        }
      } catch (error) {
        console.error('Error fetching electrification runs:', error);
      }
    };
    
    fetchElectrificationRuns();
  }, []);
  
  // Fetch electrification results when selected run changes
  const fetchElectrificationData = useCallback(async () => {
    if (!selectedRun) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all data in parallel (including metadata)
      const [summaryRes, stationRes, vehicleRes, generationRes, metadataRes] = await Promise.all([
        fetch(API_ENDPOINTS.electrificationFleetSummary(selectedRun)),
        fetch(API_ENDPOINTS.electrificationStationDetails(selectedRun)),
        fetch(API_ENDPOINTS.electrificationVehicleDetails(selectedRun)),
        fetch(API_ENDPOINTS.electrificationGenerationSummary(selectedRun)),
        fetch(API_ENDPOINTS.electrificationMetadata(selectedRun)),
      ]);
      
      // Parse fleet summary
      if (summaryRes.ok) {
        const summaryText = await summaryRes.text();
        const summaryData = parseCSV(summaryText);
        if (summaryData.length > 0) {
          const row = summaryData[0];
          setFleetSummary({
            run_id: String(row.run_id || ''),
            timestamp: String(row.timestamp || ''),
            total_fleet_size: Number(row.total_fleet_size) || 0,
            total_evs: Number(row.total_evs) || 0,
            total_diesel: Number(row.total_diesel) || 0,
            total_vehicles_converted: Number(row.total_vehicles_converted) || 0,
            conversion_type: String(row.conversion_type || ''),
            conversion_value: Number(row.conversion_value) || 0,
            target_stations: String(row.target_stations || ''),
            target_vehicle_types: String(row.target_vehicle_types || ''),
            baseline_co2_emissions: Number(row.baseline_co2_emissions) || 0,
            projected_co2_emissions: Number(row.projected_co2_emissions) || 0,
            co2_reduction_percentage: Number(row.co2_reduction_percentage) || 0,
            total_vehicle_cost: Number(row.total_vehicle_cost) || 0,
            total_infrastructure_cost: Number(row.total_infrastructure_cost) || 0,
            total_investment: Number(row.total_investment) || 0,
            electricity_cost_GBP: Number(row.electricity_cost_GBP) || 0,
            ev_dispatched: Number(row.ev_dispatched) || 0,
            diesel_dispatched: Number(row.diesel_dispatched) || 0,
            budget: String(row.budget || 'None'),
            budget_utilization: String(row.budget_utilization || '0%'),
          });
        }
      }
      
      // Parse station details
      if (stationRes.ok) {
        const stationText = await stationRes.text();
        const stationData = parseCSV(stationText);
        setStationDetails(stationData.map(row => {
          // Spread raw row first, then override with properly typed values
          const parsed: StationDetail = {
            ...row,
            station_name: String(row.station_name || ''),
            total_ev_vehicles: Number(row.total_ev_vehicles) || 0,
            total_diesel_vehicles: Number(row.total_diesel_vehicles) || 0,
            charging_points: Number(row.charging_points) || 0,
            charging_rates: String(row.charging_rates || ''),
          };
          return parsed;
        }));
      }
      
      // Parse vehicle type details
      if (vehicleRes.ok) {
        const vehicleText = await vehicleRes.text();
        const vehicleData = parseCSV(vehicleText);
        setVehicleTypeDetails(vehicleData.map(row => ({
          vehicle_type: String(row.vehicle_type || ''),
          number_converted: Number(row.number_converted) || 0,
          total_ev: Number(row.total_ev) || 0,
          total_diesel: Number(row.total_diesel) || 0,
          battery_capacity: Number(row.battery_capacity) || 0,
          cost_per_vehicle: Number(row.cost_per_vehicle) || 0,
          total_cost: Number(row.total_cost) || 0,
          electricity_cost_GBP: Number(row.electricity_cost_GBP) || 0,
        })));
      }
      
      // Parse generation summary
      if (generationRes.ok) {
        const generationText = await generationRes.text();
        const generationData = parseCSV(generationText);
        setGenerationSummary(generationData.map(row => ({
          generation: Number(row.generation) || 0,
          best_fitness: Number(row.best_fitness) || 0,
          best_performance: Number(row.best_performance) || 0,
          best_cost: Number(row.best_cost) || 0,
          population_size: Number(row.population_size) || 0,
          valid_solutions: Number(row.valid_solutions) || 0,
        })));
      }
      
      // Parse metadata (JSON)
      if (metadataRes.ok) {
        const metadata = await metadataRes.json();
        setElectrificationMetadata(metadata);
        logger.info('[Electrification] Metadata loaded:', metadata);
      } else {
        setElectrificationMetadata(null);
        logger.info('[Electrification] No metadata available for this run');
      }
      
    } catch (err) {
      console.error('Error fetching electrification data:', err);
      setError('Failed to load electrification results');
    } finally {
      setIsLoading(false);
    }
  }, [selectedRun]);
  
  useEffect(() => {
    fetchElectrificationData();
  }, [fetchElectrificationData]);
  
  // Computed fleet summary data from API results
  const fleetSummaryData = fleetSummary ? [
    { 
      metric: 'Total Fleet Size', 
      baseline: String(fleetSummary.total_fleet_size), 
      projected: String(fleetSummary.total_fleet_size), 
      change: '0%' 
    },
    { 
      metric: 'Total Diesel Vehicles', 
      baseline: String(fleetSummary.total_diesel + fleetSummary.total_evs), 
      projected: <span className="text-success">{String(fleetSummary.total_diesel)}</span>, 
      change: <span className="text-success">-{fleetSummary.total_vehicles_converted}</span> 
    },
    { 
      metric: 'Total EVs', 
      baseline: '0', 
      projected: <span className="text-success">{String(fleetSummary.total_evs)}</span>, 
      change: <span className="text-success">+{fleetSummary.total_evs}</span> 
    },
    { 
      metric: 'Diesel Vehicles Dispatched', 
      baseline: '-', 
      projected: <span className="text-muted-foreground">{String(fleetSummary.diesel_dispatched || 0)}</span>, 
      change: '-' 
    },
    { 
      metric: 'EVs Dispatched', 
      baseline: '-', 
      projected: <span className="text-success">{String(fleetSummary.ev_dispatched || 0)}</span>, 
      change: '-' 
    },
    { 
      metric: 'CO₂ Emissions (kg/year)', 
      baseline: fleetSummary.baseline_co2_emissions.toLocaleString('en-GB', { maximumFractionDigits: 0 }), 
      projected: <span className="text-success">{fleetSummary.projected_co2_emissions.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span>, 
      change: <span className="text-success">-{fleetSummary.co2_reduction_percentage.toFixed(1)}%</span> 
    },
  ] : [];

  // Cost-related data for Cost Report section
  const costReportData = fleetSummary ? [
    { 
      metric: 'Vehicle Conversion Cost', 
      baseline: (() => {
        const isEditing = editingBudget === 'Vehicle Conversion Cost';
        const currentBudget = budgetValues['Vehicle Conversion Cost'] ?? 
          (fleetSummary.budget !== 'None' ? (() => {
            const budgetNum = typeof fleetSummary.budget === 'string' 
              ? parseFloat(fleetSummary.budget.replace(/[£,\s]/g, '')) || 0
              : fleetSummary.budget || 0;
            return budgetNum;
          })() : 0);
        
        return (
          <div className="text-right" style={{ minWidth: '6rem' }}>
            {isEditing ? (
              <input
                type="text"
                defaultValue={currentBudget === 0 ? '' : `£${(currentBudget / 1000000).toFixed(2)}M`}
                placeholder="Enter budget"
                onBlur={(e) => handleBudgetEdit('Vehicle Conversion Cost', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleBudgetEdit('Vehicle Conversion Cost', (e.target as HTMLInputElement).value);
                  } else if (e.key === 'Escape') {
                    setEditingBudget(null);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="px-2 py-1 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                style={{ minHeight: '1.5rem', lineHeight: '1.5rem', boxSizing: 'border-box', width: '100%' }}
                autoFocus
              />
            ) : (
              <span 
                className="inline-block px-2 py-1 border border-transparent box-border cursor-pointer text-right hover:text-success transition-all"
                style={{ minHeight: '1.5rem', lineHeight: '1.5rem', boxSizing: 'border-box' }}
                onDoubleClick={() => setEditingBudget('Vehicle Conversion Cost')}
              >
                {currentBudget === 0 ? <span className="text-muted-foreground">No budget set</span> : `£${(currentBudget / 1000000).toFixed(2)}M`}
              </span>
            )}
          </div>
        );
      })(), 
      projected: `£${(fleetSummary.total_vehicle_cost / 1000000).toFixed(2)}M`, 
      change: (() => {
        const currentBudget = budgetValues['Vehicle Conversion Cost'] ?? 
          (fleetSummary.budget !== 'None' ? (() => {
            const budgetNum = typeof fleetSummary.budget === 'string' 
              ? parseFloat(fleetSummary.budget.replace(/[£,\s]/g, '')) || 0
              : fleetSummary.budget || 0;
            return budgetNum;
          })() : 0);
        const expenditure = fleetSummary.total_vehicle_cost;
        if (currentBudget === 0) return '-';
        const difference = currentBudget - expenditure;
        if (difference > 0) {
          return <span className="text-success">+£{(difference / 1000000).toFixed(2)}M</span>;
        } else if (difference < 0) {
          return <span className="text-destructive">£{(difference / 1000000).toFixed(2)}M</span>;
        }
        return <span className="text-success">£0.00M</span>;
      })()
    },
    { 
      metric: 'Infrastructure Cost', 
      baseline: (() => {
        const isEditing = editingBudget === 'Infrastructure Cost';
        const currentBudget = budgetValues['Infrastructure Cost'] ?? 0;
        
        return (
          <div className="text-right" style={{ minWidth: '6rem' }}>
            {isEditing ? (
              <input
                type="text"
                defaultValue={currentBudget === 0 ? '' : `£${(currentBudget / 1000000).toFixed(2)}M`}
                placeholder="Enter budget"
                onBlur={(e) => handleBudgetEdit('Infrastructure Cost', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleBudgetEdit('Infrastructure Cost', (e.target as HTMLInputElement).value);
                  } else if (e.key === 'Escape') {
                    setEditingBudget(null);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="px-2 py-1 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                style={{ minHeight: '1.5rem', lineHeight: '1.5rem', boxSizing: 'border-box', width: '100%' }}
                autoFocus
              />
            ) : (
              <span 
                className="inline-block px-2 py-1 border border-transparent box-border cursor-pointer text-right hover:text-success transition-all"
                style={{ minHeight: '1.5rem', lineHeight: '1.5rem', boxSizing: 'border-box' }}
                onDoubleClick={() => setEditingBudget('Infrastructure Cost')}
              >
                {currentBudget === 0 ? <span className="text-muted-foreground">-</span> : `£${(currentBudget / 1000000).toFixed(2)}M`}
              </span>
            )}
          </div>
        );
      })(), 
      projected: `£${(fleetSummary.total_infrastructure_cost / 1000).toFixed(0)}K`, 
      change: (() => {
        const currentBudget = budgetValues['Infrastructure Cost'] ?? 0;
        const expenditure = fleetSummary.total_infrastructure_cost;
        if (currentBudget === 0) return '-';
        const difference = currentBudget - expenditure;
        if (difference > 0) {
          return <span className="text-success">+£{(difference / 1000000).toFixed(2)}M</span>;
        } else if (difference < 0) {
          return <span className="text-destructive">£{(difference / 1000000).toFixed(2)}M</span>;
        }
        return <span className="text-success">£0.00M</span>;
      })()
    },
  ] : [];

  // Calculate totals for the sticky TOTAL row
  const costTotals = fleetSummary ? (() => {
    const vehicleCostInM = fleetSummary.total_vehicle_cost / 1000000;
    const infrastructureCostInM = fleetSummary.total_infrastructure_cost / 1000000; // Convert to millions
    return vehicleCostInM + infrastructureCostInM;
  })() : 0;

  // Get budget value for TOTAL row (use edited value if available, or sum of both budgets)
  const budgetValue = (() => {
    const editedVehicleBudget = budgetValues['Vehicle Conversion Cost'];
    const editedInfrastructureBudget = budgetValues['Infrastructure Cost'];
    
    // If both are edited, sum them
    if (editedVehicleBudget && editedInfrastructureBudget) {
      return (editedVehicleBudget + editedInfrastructureBudget) / 1000000;
    }
    
    // If only vehicle budget is edited, use that
    if (editedVehicleBudget) {
      return editedVehicleBudget / 1000000;
    }
    
    // If only infrastructure budget is edited, use that
    if (editedInfrastructureBudget) {
      return editedInfrastructureBudget / 1000000;
    }
    
    // Otherwise use the original budget from fleetSummary
    if (fleetSummary && fleetSummary.budget !== 'None') {
      const budgetNum = typeof fleetSummary.budget === 'string' 
        ? parseFloat(fleetSummary.budget.replace(/[£,\s]/g, '')) || 0
        : fleetSummary.budget || 0;
      return budgetNum / 1000000;
    }
    return 0;
  })();

  // Add TOTAL row (sticky at bottom)
  const costReportDataWithTotal = [
    ...costReportData,
    {
      metric: 'TOTAL',
      baseline: (() => {
        const isEditing = editingBudget === 'TOTAL';
        return (
          <div className="text-right" style={{ minWidth: '6rem' }}>
            {isEditing ? (
              <input
                type="text"
                defaultValue={budgetValue === 0 ? '' : `£${budgetValue.toFixed(2)}M`}
                placeholder="Enter budget"
                onBlur={(e) => handleBudgetEdit('TOTAL', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleBudgetEdit('TOTAL', (e.target as HTMLInputElement).value);
                  } else if (e.key === 'Escape') {
                    setEditingBudget(null);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="px-2 py-1 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                style={{ minHeight: '1.5rem', lineHeight: '1.5rem', boxSizing: 'border-box', width: '100%' }}
                autoFocus
              />
            ) : (
              <span 
                className="inline-block px-2 py-1 border border-transparent box-border cursor-pointer text-right hover:text-success transition-all"
                style={{ minHeight: '1.5rem', lineHeight: '1.5rem', boxSizing: 'border-box' }}
                onDoubleClick={() => setEditingBudget('TOTAL')}
              >
                {budgetValue > 0 ? `£${budgetValue.toFixed(2)}M` : <span className="text-muted-foreground">No budget set</span>}
              </span>
            )}
          </div>
        );
      })(),
      projected: `£${costTotals.toFixed(2)}M`,
      change: (() => {
        const totalBudgetInM = budgetValue;
        if (totalBudgetInM === 0) return '-';
        const difference = totalBudgetInM - costTotals;
        if (difference > 0) {
          return <span className="text-success">+£{difference.toFixed(2)}M</span>;
        } else if (difference < 0) {
          return <span className="text-destructive">£{difference.toFixed(2)}M</span>;
        }
        return <span className="text-success">£0.00M</span>;
      })(),
      _isStickyRow: true, // Make this row sticky at the bottom
    }
  ];

  // Computed station comparison data from API results
  const stationComparisonData = stationDetails.map(station => {
    const totalBefore = station.total_ev_vehicles + station.total_diesel_vehicles;
    const conversionRateNum = totalBefore > 0 ? (station.total_ev_vehicles / totalBefore) * 100 : 0;
    // Remove "Community", "Fire", and "Station" from station names for compact display
    // e.g. "Eastbourne Fire Station" → "Eastbourne", "The Ridge Community Fire Station" → "The Ridge"
    const shortName = station.station_name
      .replace(/\bCommunity\b/gi, '')
      .replace(/\bFire\b/gi, '')
      .replace(/\bStation\b/gi, '')
      .replace(/\s+/g, ' ')  // Collapse multiple spaces
      .trim();
    
    // Calculate color for EV % progress bar (green = high electrification, yellow = medium, red = low)
    const getEvColor = (rate: number) => {
      if (rate >= 70) return STATUS_COLORS.success; // green
      if (rate >= 40) return STATUS_COLORS.warning; // yellow
      return STATUS_COLORS.destructive; // red
    };
    
    return {
      station: isDemoMode ? redactStationName(shortName, isDemoMode) : shortName,
      totalBefore: totalBefore,
      evAfter: station.total_ev_vehicles,
      dieselAfter: station.total_diesel_vehicles,
      // Use utilisation_rate key to trigger progress bar in DataTable
      utilisation_rate: `${conversionRateNum.toFixed(0)}%`,
      _utilisationRateValue: conversionRateNum,
      _utilisationRateColor: getEvColor(conversionRateNum),
      chargingPoints: station.charging_points,
      chargingRate: station.charging_rates ? `${station.charging_rates} kW` : '-',
    };
  }).filter(s => s.totalBefore > 0); // Only show stations with vehicles

  // Calculate totals for the sticky TOTAL row
  const stationTotals = stationComparisonData.reduce((acc, station) => {
    acc.totalBefore += station.totalBefore;
    acc.evAfter += station.evAfter;
    acc.dieselAfter += station.dieselAfter;
    acc.chargingPoints += station.chargingPoints;
    return acc;
  }, { totalBefore: 0, evAfter: 0, dieselAfter: 0, chargingPoints: 0 });

  // Calculate overall EV percentage
  const totalEvPercentage = stationTotals.totalBefore > 0 
    ? (stationTotals.evAfter / stationTotals.totalBefore) * 100 
    : 0;

  // Calculate color for total EV % progress bar
  const getEvColor = (rate: number) => {
    if (rate >= 70) return STATUS_COLORS.success; // green
    if (rate >= 40) return STATUS_COLORS.warning; // yellow
    return STATUS_COLORS.destructive; // red
  };

  // Add TOTAL row (sticky at bottom)
  const stationComparisonDataWithTotal = [
    ...stationComparisonData,
    {
      station: 'TOTAL',
      totalBefore: stationTotals.totalBefore,
      evAfter: stationTotals.evAfter,
      dieselAfter: stationTotals.dieselAfter,
      utilisation_rate: `${totalEvPercentage.toFixed(0)}%`,
      _utilisationRateValue: totalEvPercentage,
      _utilisationRateColor: getEvColor(totalEvPercentage),
      chargingPoints: stationTotals.chargingPoints,
      chargingRate: '-',
      _isStickyRow: true, // Make this row sticky at the bottom
    }
  ];

  // Computed vehicle type summary from API results with editable unit costs
  const vehicleSummaryData = vehicleTypeDetails.map(vt => {
    const formattedType = vt.vehicle_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const displayType = isDemoMode ? redactVehicleType(vt.vehicle_type, isDemoMode) : formattedType;
    
    // Use user-configured cost if available, otherwise use backend value
    const currentUnitCost = evUnitCosts[formattedType] ?? vt.cost_per_vehicle;
    const recalculatedTotalCost = currentUnitCost * vt.total_ev;
    
    const isEditing = editingEvUnitCost?.vehicleType === formattedType;
    
    // Calculate EV percentage for this vehicle type
    const totalVehicles = vt.total_ev + vt.total_diesel;
    const evPercentage = totalVehicles > 0 ? (vt.total_ev / totalVehicles) * 100 : 0;
    
    // Calculate color for EV % progress bar (green = high electrification, yellow = medium, red = low)
    const getEvColorForVehicleType = (rate: number) => {
      if (rate >= 70) return STATUS_COLORS.success; // green
      if (rate >= 40) return STATUS_COLORS.warning; // yellow
      return STATUS_COLORS.destructive; // red
    };
    
    return {
      vehicleType: displayType,
      totalEV: vt.total_ev,
      totalDiesel: vt.total_diesel,
      batteryCapacity: `${vt.battery_capacity} kWh`,
      // Use utilisation_rate key to trigger progress bar in DataTable
      utilisation_rate: `${evPercentage.toFixed(0)}%`,
      _utilisationRateValue: evPercentage,
      _utilisationRateColor: getEvColorForVehicleType(evPercentage),
      unitCostEV: isEditing ? (
        <input
          type="number"
          step="1000"
          defaultValue={currentUnitCost}
          onBlur={(e) => handleEvUnitCostEdit(formattedType, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleEvUnitCostEdit(formattedType, (e.target as HTMLInputElement).value);
            } else if (e.key === 'Escape') {
              setEditingEvUnitCost(null);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-24 px-2 py-1 bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success box-border text-right"
          style={{ boxSizing: 'border-box' }}
          autoFocus
        />
      ) : (
        <span 
          className="inline-block w-24 px-2 py-1 border border-transparent box-border cursor-pointer text-right hover:text-success transition-all"
          style={{ boxSizing: 'border-box' }}
          onClick={() => setEditingEvUnitCost({ vehicleType: formattedType })}
        >
          £{currentUnitCost.toLocaleString()}
        </span>
      ),
      totalEVCost: `£${recalculatedTotalCost.toLocaleString()}`,
      electricityCost: vt.electricity_cost_GBP ? `£${vt.electricity_cost_GBP.toFixed(2)}` : '-',
      _originalUnitCost: vt.cost_per_vehicle,
      _formattedType: formattedType, // For export
    };
  });

  // Calculate totals for the sticky TOTAL row
  const vehicleTypeTotals = vehicleSummaryData.reduce((acc, vehicle) => {
    acc.totalEV += vehicle.totalEV;
    acc.totalDiesel += vehicle.totalDiesel;
    acc.totalCost += (evUnitCosts[vehicle._formattedType] ?? vehicle._originalUnitCost) * vehicle.totalEV;
    // Sum electricity costs (extract numeric value from formatted string)
    const elecCostStr = vehicle.electricityCost || '-';
    if (elecCostStr !== '-') {
      const elecCostNum = parseFloat(elecCostStr.replace(/[£,\s]/g, '')) || 0;
      acc.totalElectricityCost += elecCostNum;
    }
    return acc;
  }, { totalEV: 0, totalDiesel: 0, totalCost: 0, totalElectricityCost: 0 });

  // Calculate overall EV percentage
  const totalVehicleEvPercentage = (vehicleTypeTotals.totalEV + vehicleTypeTotals.totalDiesel) > 0 
    ? (vehicleTypeTotals.totalEV / (vehicleTypeTotals.totalEV + vehicleTypeTotals.totalDiesel)) * 100 
    : 0;

  // Calculate color for total EV % progress bar
  const getEvColorForTotal = (rate: number) => {
    if (rate >= 70) return STATUS_COLORS.success; // green
    if (rate >= 40) return STATUS_COLORS.warning; // yellow
    return STATUS_COLORS.destructive; // red
  };

  // Add TOTAL row (sticky at bottom)
  // Check if target is met using metadata (for percentage-based conversion targets)
  const metadataConversionType = electrificationMetadata?.optimization_metadata?.conversion_criteria?.type;
  const metadataConversionValue = electrificationMetadata?.optimization_metadata?.conversion_criteria?.value;
  const isTargetMet = metadataConversionType === 'percentage' && 
                      metadataConversionValue !== undefined && 
                      totalVehicleEvPercentage >= metadataConversionValue;
  
  const vehicleSummaryDataWithTotal = [
    ...vehicleSummaryData,
    {
      vehicleType: 'TOTAL',
      totalEV: vehicleTypeTotals.totalEV,
      totalDiesel: vehicleTypeTotals.totalDiesel,
      batteryCapacity: '-',
      utilisation_rate: `${totalVehicleEvPercentage.toFixed(0)}%`,
      _utilisationRateValue: totalVehicleEvPercentage,
      _utilisationRateColor: getEvColorForTotal(totalVehicleEvPercentage),
      _showCheckmark: isTargetMet, // Flag to show checkmark after progress bar
      _targetPercentage: metadataConversionValue, // Store target percentage for tooltip
      unitCostEV: '-',
      totalEVCost: `£${vehicleTypeTotals.totalCost.toLocaleString()}`,
      electricityCost: `£${vehicleTypeTotals.totalElectricityCost.toFixed(2)}`,
      _isStickyRow: true, // Make this row sticky at the bottom
    }
  ];
  
  // Compute charger allocation summary
  // Note: Ensure numeric conversion since CSV values come as strings
  const chargerAllocation = stationDetails.reduce((acc, station) => {
    const rate = station.charging_rates;
    const chargerCount = Number(station.charging_points) || 0;
    if (rate && chargerCount > 0) {
      const rateKey = `${rate} kW`;
      acc[rateKey] = (acc[rateKey] || 0) + chargerCount;
    }
    return acc;
  }, {} as Record<string, number>);
  
  const chargerAllocationData = Object.entries(chargerAllocation)
    .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
    .map(([rate, count]) => ({ rate, count: Number(count) }));
  
  // Calculate total chargers
  const totalChargers = Object.values(chargerAllocation).reduce((sum, count) => Number(sum) + Number(count), 0);

  return (
    <>
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid #fbbf24;
          cursor: pointer;
          box-shadow: 0 0 4px rgba(251, 191, 36, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid #fbbf24;
          cursor: pointer;
          box-shadow: 0 0 4px rgba(251, 191, 36, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          background: #fef3c7;
          box-shadow: 0 0 8px rgba(251, 191, 36, 0.8), 0 2px 6px rgba(0, 0, 0, 0.4);
        }
        input[type="range"]::-moz-range-thumb:hover {
          background: #fef3c7;
          box-shadow: 0 0 8px rgba(251, 191, 36, 0.8), 0 2px 6px rgba(0, 0, 0, 0.4);
        }
      `}</style>
    <div className="space-y-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Electrification</h2>
        <p className="text-muted-foreground text-sm">
          Review potential to switch to electric vehicles. Helps reduce fuel costs and plan long-term budgets.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <FleetStyleKpiCard
          title="Total EVs Converted"
          value={fleetSummary ? String(fleetSummary.total_evs) : '-'}
          subtitle={fleetSummary ? `of ${fleetSummary.total_fleet_size} vehicles` : 'Loading...'}
          accent="green"
        />
        <FleetStyleKpiCard
          title="CO₂ Reduction"
          value={fleetSummary ? `${fleetSummary.co2_reduction_percentage.toFixed(1)}%` : '-'}
          subtitle={fleetSummary ? `${(fleetSummary.baseline_co2_emissions - fleetSummary.projected_co2_emissions).toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg/year saved` : 'Loading...'}
          accent="amber"
        />
        <FleetStyleKpiCard
          title="Charging Points"
          value={String(totalChargers)}
          subtitle={chargerAllocationData.length > 0 ? chargerAllocationData.map(c => `${c.count}×${c.rate}`).join(', ') : 'No chargers'}
          accent="blue"
        />
        <FleetStyleKpiCard
          title="Performance Score"
          value={generationSummary.length > 0 ? `${generationSummary[generationSummary.length - 1].best_performance.toFixed(0)}%` : '-'}
          subtitle="Operational capability maintained"
          accent="purple"
        />
      </div>

      {/* User Configuration Section */}
      <div className="mb-6 bg-gradient-to-br from-card/60 to-card/40 rounded-lg p-6 border-2 border-success/40 hover:border-success/60 hover:shadow-lg hover:shadow-success/20 transition-all">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            User Configuration
          </h3>
          <div className="flex items-center gap-2">
            {/* Info icon with tooltip */}
            <div className="relative group">
              <HelpCircle 
                className="w-5 h-5 text-muted-foreground hover:text-success cursor-help transition-colors" 
                aria-label="Electrification configuration information"
              />
              <div className="absolute right-0 top-6 w-64 p-3 bg-popover border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                <div className="text-xs text-muted-foreground space-y-2">
                  <div className="font-semibold text-white mb-2">Electrification Configuration:</div>
                  <div className="space-y-1">
                    <div>• <strong>Conversion target:</strong> Set percentage (of specified vehicle types if selected, otherwise entire fleet) or number of vehicles to convert</div>
                    <div>• <strong>Budget constraints:</strong> Define total budget and max unit cost limits</div>
                    <div>• <strong>Priority criteria:</strong> Choose what factors to prioritise in conversion</div>
                    <div>• <strong>Conversion scope:</strong> Select specific vehicles, types, or stations</div>
                    <div>• <strong>Timeline:</strong> Plan conversion phases and duration</div>
                  </div>
                  <div className="text-success text-xs mt-2 pt-2 border-t border-border">
                    💡 Tip: Start with a smaller scope to test conversion plans
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                const resultsSection = document.getElementById('electrification-results-section');
                if (resultsSection) {
                  resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="px-4 py-2 bg-success hover:bg-success text-background text-sm font-medium rounded-md border border-success/60 hover:border-success/60 hover:shadow-lg hover:shadow-success/60 transition-all"
            >
              Generate Conversion Plan
            </button>
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Basic Settings Row - Budget and Data Source */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 hover:shadow-lg hover:shadow-success/40 hover:border-success/40 border border-transparent rounded-lg p-2 transition-all">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">Total Budget (£)</label>
              <input
                type="number"
                min="0"
                step="10000"
                value={totalBudget}
                onChange={(e) => setTotalBudget(Math.max(0, parseInt(e.target.value) || 0))}
                className="px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">Maximum Unit Cost (£)</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={maxUnitCost}
                onChange={(e) => setMaxUnitCost(Math.max(0, parseInt(e.target.value) || 0))}
                className="px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
              />
            </div>
            {/* Data Model Source selector moved to Basic Settings row (second row), spans 2 columns */}
            <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Data Model Source</label>
              <select
                value={dataModelSource}
                onChange={(e) => setDataModelSource(e.target.value as DataModelSource)}
                className="px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
              >
                <option value="utilization">1a. Incident (Electrify for the Past)</option>
                <option value="simConfig">1b. Incident (Electrify for the Future)</option>
                <option value="electrificationRun">2. Telematics (Electrify for the Future)</option>
              </select>
            </div>
          </div>

          {/* Conversion Scope Configuration */}
          <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-success/40 hover:border-success/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
            <h4 className="text-sm font-semibold text-white mb-3">Conversion Scope</h4>
            <p className="text-xs text-muted-foreground mb-4">
              Define conversion targets and select specific vehicles, vehicle types, or stations to include. Leave selections empty to convert the entire fleet.
            </p>
            
            {/* Conversion Target - Moved from Basic Settings for logical grouping */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-muted-foreground">Conversion Target Type</label>
                </div>
                <select
                  value={conversionTarget}
                  onChange={(e) => setConversionTarget(e.target.value as 'percentage' | 'vehicles' | 'custom')}
                  className="px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                >
                  <option value="percentage">By Percentage</option>
                  <option value="vehicles">By Number of Vehicles</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            
            {conversionTarget === 'percentage' ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Target Percentage (%)</label>
                  <div className="relative group">
                    <HelpCircle 
                      className="w-3.5 h-3.5 text-muted-foreground hover:text-success cursor-help transition-colors" 
                      aria-label="Target percentage information"
                    />
                    <div className="absolute left-0 top-5 w-64 p-3 bg-popover border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                      <div className="text-xs text-muted-foreground space-y-2">
                        <div className="font-semibold text-white mb-1">Target Percentage Scope:</div>
                        <div>
                          Percentage applies to each selected vehicle type. If no types selected, applies to entire fleet.
                        </div>
                        <div className="text-success text-xs mt-2 pt-2 border-t border-border">
                          💡 Example: 60% with "Pumping Appliance" selected converts 60% of pumping appliances
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={targetPercentage}
                  onChange={(e) => setTargetPercentage(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                  className="px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                />
              </div>
            ) : conversionTarget === 'vehicles' ? (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Target Number of Vehicles</label>
                <input
                  type="number"
                  value={selectedVehicles.length}
                  readOnly
                  className="px-3 py-2 bg-accent text-white text-sm rounded border border-input cursor-not-allowed"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Target (Custom Selection)</label>
                <input
                  type="text"
                  value="Manual Selection"
                  readOnly
                  className="px-3 py-2 bg-accent text-muted-foreground text-sm rounded border border-input cursor-not-allowed"
                />
              </div>
            )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Vehicle Selection */}
              <div className={`bg-card/50 rounded-lg p-4 border border-border transition-all ${
                conversionTarget === 'percentage'
                  ? 'opacity-50 cursor-not-allowed border-border'
                  : 'hover:border-success/60 hover:shadow-lg hover:shadow-success/40'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-white">Vehicles</label>
                  {conversionTarget === 'percentage' && (
                    <span className="text-xs text-warning">(Disabled)</span>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto border border-border rounded p-2 bg-background/50">
                  {availableVehicles.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No vehicles available</p>
                  ) : (
                    <>
                      <div className="mb-2 pb-2 border-b border-border flex items-center justify-between">
                        <button
                          type="button"
                          disabled={conversionTarget === 'percentage'}
                          onClick={() => {
                            if (selectedVehicles.length === availableVehicles.length) {
                              setSelectedVehicles([]);
                            } else {
                              setSelectedVehicles([...availableVehicles]);
                            }
                          }}
                          className="text-xs text-success hover:text-success disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {selectedVehicles.length === availableVehicles.length ? 'Deselect All' : 'Select All'}
                        </button>
                        {selectedVehicles.length > 0 && (
                          <span className="text-xs text-success">
                            ({selectedVehicles.length} vehicle{selectedVehicles.length !== 1 ? 's' : ''} selected)
                          </span>
                        )}
                      </div>
                      {availableVehicles.map(vehicleId => (
                        <label key={vehicleId} className={`flex items-center gap-2 py-1 text-xs text-muted-foreground ${
                          conversionTarget === 'percentage' ? 'cursor-not-allowed opacity-50' : 'hover:text-white cursor-pointer'
                        }`}>
                          <input
                            type="checkbox"
                            disabled={conversionTarget === 'percentage'}
                            checked={selectedVehicles.includes(vehicleId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVehicles([...selectedVehicles, vehicleId]);
                              } else {
                                setSelectedVehicles(selectedVehicles.filter(v => v !== vehicleId));
                              }
                            }}
                            className="w-4 h-4 text-success bg-secondary border-input rounded focus:ring-success focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span>{isDemoMode ? redactVehicleId(vehicleId, isDemoMode) : vehicleId}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Vehicle Type Selection */}
              <div className={`bg-card/50 rounded-lg p-4 border border-border transition-all ${
                conversionTarget === 'vehicles'
                  ? 'opacity-50 cursor-not-allowed border-border'
                  : 'hover:border-success/60 hover:shadow-lg hover:shadow-success/40'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-white">Vehicle Types</label>
                  {conversionTarget === 'vehicles' && (
                    <span className="text-xs text-warning">(Disabled)</span>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto border border-border rounded p-2 bg-background/50">
                  {availableVehicleTypes.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No vehicle types available</p>
                  ) : (
                    <>
                      <div className="mb-2 pb-2 border-b border-border flex items-center justify-between">
                        <button
                          type="button"
                          disabled={conversionTarget === 'vehicles'}
                          onClick={() => {
                            if (selectedVehicleTypes.length === availableVehicleTypes.length) {
                              setSelectedVehicleTypes([]);
                            } else {
                              setSelectedVehicleTypes([...availableVehicleTypes]);
                            }
                          }}
                          className="text-xs text-success hover:text-success disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {selectedVehicleTypes.length === availableVehicleTypes.length ? 'Deselect All' : 'Select All'}
                        </button>
                        {selectedVehicleTypes.length > 0 && (
                          <span className="text-xs text-success">
                            ({selectedVehicleTypes.length} type{selectedVehicleTypes.length !== 1 ? 's' : ''} selected)
                          </span>
                        )}
                      </div>
                      {availableVehicleTypes.map(vehicleType => (
                        <label key={vehicleType} className={`flex items-center gap-2 py-1 text-xs text-muted-foreground ${
                          conversionTarget === 'vehicles' ? 'cursor-not-allowed opacity-50' : 'hover:text-white cursor-pointer'
                        }`}>
                          <input
                            type="checkbox"
                            disabled={conversionTarget === 'vehicles'}
                            checked={selectedVehicleTypes.includes(vehicleType)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVehicleTypes([...selectedVehicleTypes, vehicleType]);
                              } else {
                                setSelectedVehicleTypes(selectedVehicleTypes.filter(t => t !== vehicleType));
                              }
                            }}
                            className="w-4 h-4 text-success bg-secondary border-input rounded focus:ring-success focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span>{isDemoMode ? redactVehicleType(vehicleType, isDemoMode) : vehicleType}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Station Selection */}
              <div className={`bg-card/50 rounded-lg p-4 border border-border transition-all ${
                conversionTarget === 'percentage' || conversionTarget === 'vehicles'
                  ? 'opacity-50 cursor-not-allowed border-border'
                  : 'hover:border-success/60 hover:shadow-lg hover:shadow-success/40'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-white">Stations</label>
                  {(conversionTarget === 'percentage' || conversionTarget === 'vehicles') && (
                    <span className="text-xs text-warning">(Disabled)</span>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto border border-border rounded p-2 bg-background/50">
                  {availableStations.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No stations available</p>
                  ) : (
                    <>
                      <div className="mb-2 pb-2 border-b border-border flex items-center justify-between">
                        <button
                          type="button"
                          disabled={conversionTarget === 'percentage' || conversionTarget === 'vehicles'}
                          onClick={() => {
                            if (selectedStations.length === availableStations.length) {
                              setSelectedStations([]);
                            } else {
                              setSelectedStations([...availableStations]);
                            }
                          }}
                          className="text-xs text-success hover:text-success disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {selectedStations.length === availableStations.length ? 'Deselect All' : 'Select All'}
                        </button>
                        {selectedStations.length > 0 && (
                          <span className="text-xs text-success">
                            ({selectedStations.length} station{selectedStations.length !== 1 ? 's' : ''} selected)
                          </span>
                        )}
                      </div>
                      {availableStations.map(station => (
                        <label key={station} className={`flex items-center gap-2 py-1 text-xs text-muted-foreground ${
                          conversionTarget === 'percentage' || conversionTarget === 'vehicles' ? 'cursor-not-allowed opacity-50' : 'hover:text-white cursor-pointer'
                        }`}>
                          <input
                            type="checkbox"
                            disabled={conversionTarget === 'percentage' || conversionTarget === 'vehicles'}
                            checked={selectedStations.includes(station)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStations([...selectedStations, station]);
                              } else {
                                setSelectedStations(selectedStations.filter(s => s !== station));
                              }
                            }}
                            className="w-4 h-4 text-success bg-secondary border-input rounded focus:ring-success focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span>{isDemoMode ? redactStationName(station, isDemoMode) : station}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Priorities */}
          <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-success/40 hover:border-success/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
            <h4 className="text-sm font-semibold text-white mb-3">Conversion Priorities</h4>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground">
                Adjust the relative importance of each factor when selecting vehicles for electrification conversion.<br />
                Higher values mean that factor is prioritised more in the conversion strategy.
              </p>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${
                  (optimizationPriorities.performance + optimizationPriorities.cost + optimizationPriorities.environmental + optimizationPriorities.charging) === 100
                    ? 'text-success'
                    : 'text-warning'
                }`}>
                  Total: {optimizationPriorities.performance + optimizationPriorities.cost + optimizationPriorities.environmental + optimizationPriorities.charging}%
                </span>
                <button
                  onClick={() => setOptimizationPriorities({ ...defaultOptimizationPriorities })}
                  className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-secondary rounded-md hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 transition-all border border-input"
                >
                  Reset to Default
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Performance Priority */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <div className="text-xs font-medium text-muted-foreground mb-3">Performance</div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={optimizationPriorities.performance}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = optimizationPriorities.cost + optimizationPriorities.environmental + optimizationPriorities.charging;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        // Scale other values proportionally
                        const scale = remaining / total;
                        setOptimizationPriorities({
                          performance: newValue,
                          cost: Math.round(optimizationPriorities.cost * scale),
                          environmental: Math.round(optimizationPriorities.environmental * scale),
                          charging: remaining - Math.round(optimizationPriorities.cost * scale) - Math.round(optimizationPriorities.environmental * scale),
                        });
                      } else {
                        setOptimizationPriorities({
                          ...optimizationPriorities,
                          performance: newValue,
                        });
                      }
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        ${STATUS_COLORS.destructive} 0%, 
                        ${STATUS_COLORS.warning} ${Math.min(50, optimizationPriorities.performance)}%, 
                        ${optimizationPriorities.performance > 50 ? `${STATUS_COLORS.success} ${optimizationPriorities.performance}%,` : ''}
                        rgb(75, 85, 99) ${optimizationPriorities.performance}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingPriority === 'performance' ? (
                      <input
                        type="text"
                        defaultValue={optimizationPriorities.performance}
                        onBlur={(e) => handlePriorityEdit('performance', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePriorityEdit('performance', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingPriority(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingPriority('performance')}
                      >
                        {optimizationPriorities.performance}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Cost Priority */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <div className="text-xs font-medium text-muted-foreground mb-3">Cost</div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={optimizationPriorities.cost}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = optimizationPriorities.performance + optimizationPriorities.environmental + optimizationPriorities.charging;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        const scale = remaining / total;
                        setOptimizationPriorities({
                          cost: newValue,
                          performance: Math.round(optimizationPriorities.performance * scale),
                          environmental: Math.round(optimizationPriorities.environmental * scale),
                          charging: remaining - Math.round(optimizationPriorities.performance * scale) - Math.round(optimizationPriorities.environmental * scale),
                        });
                      } else {
                        setOptimizationPriorities({
                          ...optimizationPriorities,
                          cost: newValue,
                        });
                      }
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        ${STATUS_COLORS.destructive} 0%, 
                        ${STATUS_COLORS.warning} ${Math.min(50, optimizationPriorities.cost)}%, 
                        ${optimizationPriorities.cost > 50 ? `${STATUS_COLORS.success} ${optimizationPriorities.cost}%,` : ''}
                        rgb(75, 85, 99) ${optimizationPriorities.cost}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingPriority === 'cost' ? (
                      <input
                        type="text"
                        defaultValue={optimizationPriorities.cost}
                        onBlur={(e) => handlePriorityEdit('cost', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePriorityEdit('cost', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingPriority(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingPriority('cost')}
                      >
                        {optimizationPriorities.cost}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Environmental Priority */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <div className="text-xs font-medium text-muted-foreground mb-3">Environmental</div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={optimizationPriorities.environmental}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = optimizationPriorities.performance + optimizationPriorities.cost + optimizationPriorities.charging;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        const scale = remaining / total;
                        setOptimizationPriorities({
                          environmental: newValue,
                          performance: Math.round(optimizationPriorities.performance * scale),
                          cost: Math.round(optimizationPriorities.cost * scale),
                          charging: remaining - Math.round(optimizationPriorities.performance * scale) - Math.round(optimizationPriorities.cost * scale),
                        });
                      } else {
                        setOptimizationPriorities({
                          ...optimizationPriorities,
                          environmental: newValue,
                        });
                      }
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        ${STATUS_COLORS.destructive} 0%, 
                        ${STATUS_COLORS.warning} ${Math.min(50, optimizationPriorities.environmental)}%, 
                        ${optimizationPriorities.environmental > 50 ? `${STATUS_COLORS.success} ${optimizationPriorities.environmental}%,` : ''}
                        rgb(75, 85, 99) ${optimizationPriorities.environmental}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingPriority === 'environmental' ? (
                      <input
                        type="text"
                        defaultValue={optimizationPriorities.environmental}
                        onBlur={(e) => handlePriorityEdit('environmental', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePriorityEdit('environmental', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingPriority(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingPriority('environmental')}
                      >
                        {optimizationPriorities.environmental}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Charging Priority */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <div className="text-xs font-medium text-muted-foreground mb-3">Charging</div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={optimizationPriorities.charging}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = optimizationPriorities.performance + optimizationPriorities.cost + optimizationPriorities.environmental;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        const scale = remaining / total;
                        setOptimizationPriorities({
                          charging: newValue,
                          performance: Math.round(optimizationPriorities.performance * scale),
                          cost: Math.round(optimizationPriorities.cost * scale),
                          environmental: remaining - Math.round(optimizationPriorities.performance * scale) - Math.round(optimizationPriorities.cost * scale),
                        });
                      } else {
                        setOptimizationPriorities({
                          ...optimizationPriorities,
                          charging: newValue,
                        });
                      }
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        ${STATUS_COLORS.destructive} 0%, 
                        ${STATUS_COLORS.warning} ${Math.min(50, optimizationPriorities.charging)}%, 
                        ${optimizationPriorities.charging > 50 ? `${STATUS_COLORS.success} ${optimizationPriorities.charging}%,` : ''}
                        rgb(75, 85, 99) ${optimizationPriorities.charging}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingPriority === 'charging' ? (
                      <input
                        type="text"
                        defaultValue={optimizationPriorities.charging}
                        onBlur={(e) => handlePriorityEdit('charging', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePriorityEdit('charging', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingPriority(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingPriority('charging')}
                      >
                        {optimizationPriorities.charging}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Priority Criteria */}
          <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-success/40 hover:border-success/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
            <h4 className="text-sm font-semibold text-white mb-3">Priority Criteria</h4>
            <p className="text-xs text-muted-foreground mb-4">
              Select which factors should be prioritised when choosing vehicles for conversion.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <label className="flex items-center gap-3 bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all cursor-pointer">
                <input
                  type="radio"
                  name="priorityCriteria"
                  checked={prioritizeOperationalEfficiency}
                  onChange={() => {
                    setPrioritizeOperationalEfficiency(true);
                    setPrioritizeEnvironmental(false);
                    setPrioritizeBalanced(false);
                    setOptimizationPriorities({ ...priorityWeightings.operationalEfficiency });
                  }}
                  className="w-5 h-5 text-success bg-secondary border-input rounded focus:ring-success focus:ring-2"
                />
                <div>
                  <div className="text-sm font-medium text-white">Operational Efficiency</div>
                  <div className="text-xs text-muted-foreground">Prioritise high-usage vehicles for cost and fuel savings</div>
                </div>
              </label>

              <label className="flex items-center gap-3 bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all cursor-pointer">
                <input
                  type="radio"
                  name="priorityCriteria"
                  checked={prioritizeEnvironmental}
                  onChange={() => {
                    setPrioritizeOperationalEfficiency(false);
                    setPrioritizeEnvironmental(true);
                    setPrioritizeBalanced(false);
                    setOptimizationPriorities({ ...priorityWeightings.environmental });
                  }}
                  className="w-5 h-5 text-success bg-secondary border-input rounded focus:ring-success focus:ring-2"
                />
                <div>
                  <div className="text-sm font-medium text-white">Environmental Friendliness</div>
                  <div className="text-xs text-muted-foreground">Prioritise sustainability, emissions, and eco-impact</div>
                </div>
              </label>

              <label className="flex items-center gap-3 bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all cursor-pointer">
                <input
                  type="radio"
                  name="priorityCriteria"
                  checked={prioritizeBalanced}
                  onChange={() => {
                    setPrioritizeOperationalEfficiency(false);
                    setPrioritizeEnvironmental(false);
                    setPrioritizeBalanced(true);
                    setOptimizationPriorities({ ...priorityWeightings.balanced });
                  }}
                  className="w-5 h-5 text-success bg-secondary border-input rounded focus:ring-success focus:ring-2"
                />
                <div>
                  <div className="text-sm font-medium text-white">Balanced Approach</div>
                  <div className="text-xs text-muted-foreground">Equal weighting across performance, cost, and environment</div>
                </div>
              </label>
            </div>
          </div>

          {/* Timeline Configuration */}
          <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-success/40 hover:border-success/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
            <h4 className="text-sm font-semibold text-white mb-3">Conversion Timeline</h4>
            <p className="text-xs text-muted-foreground mb-4">
              Plan the conversion phases and timeline for electrification rollout.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <label className="text-sm font-medium text-white block mb-2">
                  Number of Phases
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  How many phases to split the conversion into
                </p>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={conversionPhases}
                  onChange={(e) => setConversionPhases(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  className="w-full px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                />
              </div>

              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <label className="text-sm font-medium text-white block mb-2">
                  Phase Duration (months)
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  Duration of each conversion phase
                </p>
                <input
                  type="number"
                  min="1"
                  max="36"
                  value={phaseDurationMonths}
                  onChange={(e) => setPhaseDurationMonths(Math.max(1, Math.min(36, parseInt(e.target.value) || 12)))}
                  className="w-full px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                />
              </div>

              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success/60 hover:shadow-lg hover:shadow-success/40 transition-all">
                <label className="text-sm font-medium text-white block mb-2">
                  Start Date
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  When to begin the conversion process
                </p>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary text-white text-sm rounded border border-input focus:outline-none focus:ring-2 focus:ring-success hover:border-accent transition-all"
                />
              </div>
            </div>
          </div>

          {/* Conversion Assumptions */}
          <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-success/40 hover:border-success/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
            <h4 className="text-sm font-semibold text-white mb-3">
              Conversion Assumptions
            </h4>
            <div className="bg-card/30 rounded-lg p-4 border border-border/50">
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span><strong className="text-white">Usage-based prioritisation:</strong> Vehicles with higher utilisation are prioritised for conversion to maximise impact.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span><strong className="text-white">Cost-benefit analysis:</strong> Conversion recommendations consider fuel savings, maintenance costs, and vehicle purchase costs.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span><strong className="text-white">Operational compatibility:</strong> EV replacements maintain the same operational capabilities as diesel vehicles.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span><strong className="text-white">Budget constraints:</strong> Total conversion cost stays within specified budget limits.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span><strong className="text-white">Phased rollout:</strong> Conversion is planned across multiple phases to minimise operational disruption.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success mt-0.5">•</span>
                  <span><strong className="text-white">Emissions reduction:</strong> CO2e emissions are calculated based on vehicle usage patterns and fuel consumption.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Divider and Results Section Header */}
      <div id="electrification-results-section" className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center">
          <div className="bg-background px-4">
            <h3 className="text-lg font-semibold text-white">
              Electrification Results
            </h3>
          </div>
        </div>
      </div>

      <section>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Fleet Conversion Plan</h2>
          <p className="text-muted-foreground text-sm">
            Results from the electrification optimisation. Based on operational simulation to maintain service levels while maximising EV adoption.
          </p>
        </div>
        
        {/* Run Selector and Actions */}
        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-50 max-w-xs">
          <label className="block text-sm font-medium text-muted-foreground mb-2">
              Electrification Run
          </label>
            <select 
              value={selectedRun}
              onChange={(e) => setSelectedRun(e.target.value)}
              className="w-full px-3 py-2 border border-input bg-secondary text-white rounded-md shadow-sm focus:outline-none focus:ring-success focus:border-success"
            >
              {electrificationRuns.length === 0 ? (
                <option value="">No runs available</option>
              ) : (
                electrificationRuns.map(run => (
                  <option key={run} value={run}>{run.replace(/_/g, ' ').replace('electrification ', 'Run ')}</option>
                ))
              )}
          </select>
        </div>

          <button
            onClick={fetchElectrificationData}
            disabled={isLoading || !selectedRun}
            className="px-4 py-2 bg-secondary hover:bg-accent text-white text-sm font-medium rounded-md border border-input hover:border-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          {selectedRun && (
            <a
              href={API_ENDPOINTS.electrificationReportPDF(selectedRun)}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-primary hover:bg-primary text-white text-sm font-medium rounded-md border border-success hover:border-success transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download PDF Report
            </a>
          )}
        </div>

        {/* Loading/Error States */}
        {isLoading && (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-success animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading electrification results...</p>
          </div>
        )}
        
        {error && (
          <div className="bg-destructive/30 border border-destructive rounded-lg p-4 mb-6">
            <p className="text-destructive">{error}</p>
          </div>
        )}
        
        {!isLoading && !error && fleetSummary && (
        <div className="space-y-6">
            {/* Optimization Metadata (User Configuration) - Displayed first under Results */}
            {electrificationMetadata?.optimization_metadata && (
              <div className="mb-6 bg-card/50 rounded-lg p-6 border border-border">
                <h4 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                  <span className="text-success">⚙️</span>
                  Electrification Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Conversion Criteria */}
                  <div className="bg-background/50 rounded p-3 border border-border/50">
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Conversion Target</div>
                    <div className="text-sm font-medium text-white">
                      {electrificationMetadata.optimization_metadata.conversion_criteria?.type === 'percentage' ? (
                        (() => {
                          const targetTypes = electrificationMetadata.optimization_metadata.conversion_criteria?.target_vehicle_types;
                          const hasSpecificTypes = Array.isArray(targetTypes) && targetTypes.length > 0;
                          return hasSpecificTypes
                            ? `${electrificationMetadata.optimization_metadata.conversion_criteria?.value}% of specified vehicle types`
                            : `${electrificationMetadata.optimization_metadata.conversion_criteria?.value}% of fleet`;
                        })()
                      ) : (
                        `${electrificationMetadata.optimization_metadata.conversion_criteria?.value} vehicles`
                      )}
                    </div>
                  </div>

                  {/* Priority Option */}
                  <div className="bg-background/50 rounded p-3 border border-border/50">
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Priority Option</div>
                    <div className="text-sm font-medium text-white capitalize">
                      {electrificationMetadata.optimization_metadata.priority?.replace('_', ' ') || 'Balanced'}
                    </div>
                  </div>

                  {/* Optimization Scope */}
                  <div className="bg-background/50 rounded p-3 border border-border/50">
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Conversion Scope</div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>
                        <span className="text-muted-foreground">Types:</span>{' '}
                        {Array.isArray(electrificationMetadata.optimization_metadata.conversion_criteria?.target_vehicle_types)
                          ? electrificationMetadata.optimization_metadata.conversion_criteria.target_vehicle_types.length > 0
                            ? electrificationMetadata.optimization_metadata.conversion_criteria.target_vehicle_types.join(', ')
                            : 'All types'
                          : 'All types'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Stations:</span>{' '}
                        {Array.isArray(electrificationMetadata.optimization_metadata.conversion_criteria?.target_stations)
                          ? electrificationMetadata.optimization_metadata.conversion_criteria.target_stations.length > 0
                            ? electrificationMetadata.optimization_metadata.conversion_criteria.target_stations.join(', ')
                            : 'All stations'
                          : 'All stations'}
                      </div>
                    </div>
                  </div>

                  {/* Conversion Priorities */}
                  <div className="bg-background/50 rounded p-3 border border-border/50">
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Conversion Priorities</div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>
                        <span className="text-muted-foreground">Performance:</span>{' '}
                        <span className="text-success font-medium">
                          {electrificationMetadata.optimization_metadata.fitness_weights?.performance_percentage || 'N/A'}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Cost:</span>{' '}
                        <span className="text-ring font-medium">
                          {electrificationMetadata.optimization_metadata.fitness_weights?.cost_percentage || 'N/A'}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Environmental:</span>{' '}
                        <span className="text-success font-medium">
                          {electrificationMetadata.optimization_metadata.fitness_weights?.environmental_percentage || 'N/A'}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Charging:</span>{' '}
                        <span className="text-warning font-medium">
                          {electrificationMetadata.optimization_metadata.fitness_weights?.charging_percentage || 'N/A'}%
                        </span>
                      </div>
                      {electrificationMetadata.optimization_metadata.conversion_criteria?.prioritize_ev_dispatch && (
                        <div>
                          <span className="text-muted-foreground">EV Dispatch Priority:</span>{' '}
                          <span className="text-success font-medium">Enabled</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Simulation Settings */}
                  <div className="bg-background/50 rounded p-3 border border-border/50">
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Simulation Settings</div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>
                        <span className="text-muted-foreground">Data source:</span>{' '}
                        {electrificationMetadata.optimization_metadata.data_model_source 
                          ? electrificationMetadata.optimization_metadata.data_model_source.charAt(0).toUpperCase() + 
                            electrificationMetadata.optimization_metadata.data_model_source.slice(1)
                          : 'N/A'}
                      </div>
                      {electrificationMetadata.optimization_metadata.run_information?.simulation_duration_hours !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Simulation duration:</span>{' '}
                          {electrificationMetadata.optimization_metadata.run_information.simulation_duration_hours <= 24
                            ? `${electrificationMetadata.optimization_metadata.run_information.simulation_duration_hours} hours`
                            : `${(electrificationMetadata.optimization_metadata.run_information.simulation_duration_hours / 24).toFixed(1)} days`}
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Budget:</span>{' '}
                        {electrificationMetadata.optimization_metadata.budget_constraint?.budget_formatted || 'No limit'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Perf. threshold:</span>{' '}
                        {electrificationMetadata.optimization_metadata.ga_parameters?.performance_threshold ?? 'N/A'}%
                      </div>
                    </div>
                  </div>

                  {/* Run Information */}
                  <div className="bg-background/50 rounded p-3 border border-border/50">
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Run Information</div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>
                        <span className="text-muted-foreground">Total runs:</span>{' '}
                        {electrificationMetadata.optimization_metadata.run_information?.total_generations ?? 'N/A'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Region:</span>{' '}
                        {electrificationMetadata.optimization_metadata.region ?? 'N/A'}
                      </div>
                      {electrificationMetadata.optimization_metadata.timestamp && (
                        <div>
                          <span className="text-muted-foreground">Generated:</span>{' '}
                          {new Date(electrificationMetadata.optimization_metadata.timestamp).toLocaleString()}
                        </div>
                      )}
                      {electrificationMetadata.optimization_metadata.run_information?.elapsed_time !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Running time:</span>{' '}
                          {electrificationMetadata.optimization_metadata.run_information.elapsed_time < 60
                            ? `${electrificationMetadata.optimization_metadata.run_information.elapsed_time.toFixed(1)} s`
                            : `${(electrificationMetadata.optimization_metadata.run_information.elapsed_time / 60).toFixed(1)} mins`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
          <DataTable
            title="Conversion Summary"
            columns={[
              { key: 'metric', label: 'Metric' },
                { key: 'baseline', label: 'Before', align: 'right' as const },
                { key: 'projected', label: 'After', align: 'right' as const },
                { key: 'change', label: 'Change', align: 'right' as const },
            ]}
            data={fleetSummaryData}
              onExport={() => {
                const csv = ['Metric,Before,After,Change', ...fleetSummaryData.map(r => {
                  // Extract text from JSX elements for export
                  const getText = (value: any): string => {
                    if (typeof value === 'string') return value;
                    if (value && typeof value === 'object' && 'props' in value) {
                      // React element - extract children text
                      if (value.props && value.props.children) {
                        return String(value.props.children);
                      }
                      return String(value);
                    }
                    return String(value || '');
                  };
                  const projectedText = getText(r.projected);
                  const changeText = getText(r.change);
                  return `${r.metric},${r.baseline},${projectedText},${changeText}`;
                })].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'fleet_summary.csv';
                a.click();
              }}
          />

          <DataTable
            title="Station Conversion Summary"
            columns={[
              { key: 'station', label: 'Station' },
                { key: 'totalBefore', label: 'Total Vehicles', align: 'right' as const },
                { key: 'evAfter', label: 'Converted EVs', align: 'right' as const },
                { key: 'dieselAfter', label: 'Diesel', align: 'right' as const },
                { key: 'chargingPoints', label: 'Chargers', align: 'right' as const },
                { key: 'chargingRate', label: 'Charger Speed', align: 'right' as const },
            ]}
            data={stationComparisonDataWithTotal}
              equalWidthColumns={true}
              onExport={() => {
                const csv = ['Station,Total Vehicles,EVs,Diesel,Chargers,Charger Speed', ...stationComparisonDataWithTotal.map(r => `${r.station},${r.totalBefore},${r.evAfter},${r.dieselAfter},${r.chargingPoints},${r.chargingRate}`)].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'station_conversion_summary.csv';
                a.click();
              }}
          />

          <div>
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-white mb-4">Cost Report for Electrification</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Individual vehicle type summary showing which vehicles were converted to electric during electrification.
              </p>
            </div>
            
            {/* Cost Summary Table */}
            {costReportData.length > 0 && (
              <div className="mb-6">
                <DataTable
                  title="Cost Summary"
                  columns={[
                    { key: 'metric', label: 'Metric' },
                    { 
                      key: 'baseline', 
                      label: (
                        <div className="flex items-center gap-2 w-full" style={{ paddingRight: '8px', paddingLeft: '8px' }}>
                          <span>Budget</span>
                          <Pencil className="w-3.5 h-3.5 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help flex-shrink-0" />
                        </div>
                      ), 
                      align: 'right' as const 
                    },
                    { key: 'projected', label: 'Expected Expenditure', align: 'right' as const },
                    { key: 'change', label: 'Budget Met?', align: 'right' as const },
                  ]}
                  data={costReportDataWithTotal}
                  onExport={() => {
                    const csv = ['Metric,Budget,Expected Expenditure,Budget Met?', ...costReportDataWithTotal.map(r => {
                      // Extract text from JSX elements for export
                      const getText = (value: any): string => {
                        if (typeof value === 'string') return value;
                        if (value && typeof value === 'object' && 'props' in value) {
                          // React element - extract children text
                          if (value.props && value.props.children) {
                            return String(value.props.children);
                          }
                          return String(value);
                        }
                        return String(value || '');
                      };
                      // For budget column, use edited value if available, otherwise extract from JSX
                      let baselineText: string;
                      if (r.metric === 'Vehicle Conversion Cost' && budgetValues['Vehicle Conversion Cost']) {
                        baselineText = `£${(budgetValues['Vehicle Conversion Cost'] / 1000000).toFixed(2)}M`;
                      } else if (r.metric === 'Infrastructure Cost' && budgetValues['Infrastructure Cost']) {
                        baselineText = `£${(budgetValues['Infrastructure Cost'] / 1000000).toFixed(2)}M`;
                      } else if (r.metric === 'TOTAL') {
                        // For TOTAL, sum both budgets if available
                        const vehicleBudget = budgetValues['Vehicle Conversion Cost'] || 0;
                        const infrastructureBudget = budgetValues['Infrastructure Cost'] || 0;
                        if (vehicleBudget || infrastructureBudget) {
                          const totalBudget = (vehicleBudget + infrastructureBudget) / 1000000;
                          baselineText = `£${totalBudget.toFixed(2)}M`;
                        } else {
                          baselineText = getText(r.baseline);
                        }
                      } else {
                        baselineText = getText(r.baseline);
                      }
                      const projectedText = getText(r.projected);
                      let changeText = getText(r.change);
                      // If changeText contains JSX span content, extract the numeric value
                      if (changeText && typeof r.change !== 'string' && r.change && typeof r.change === 'object' && 'props' in r.change) {
                        // Extract the actual text content from the span
                        const spanContent = r.change.props?.children;
                        if (typeof spanContent === 'string') {
                          changeText = spanContent;
                        } else if (Array.isArray(spanContent)) {
                          changeText = spanContent.join('');
                        }
                      }
                      return `${r.metric},${baselineText},${projectedText},${changeText || '-'}`;
                    })].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'cost_report.csv';
                    a.click();
                  }}
                />
              </div>
            )}

          <DataTable
            title="Vehicle Type Summary"
            columns={[
              { key: 'vehicleType', label: 'Vehicle Type' },
                { key: 'totalEV', label: 'Converted EVs', align: 'right' as const },
                { key: 'totalDiesel', label: 'Diesel', align: 'right' as const },
                { key: 'utilisation_rate', label: 'EV %', align: 'right' as const },
                { key: 'batteryCapacity', label: 'Battery', align: 'right' as const },
                { 
                  key: 'unitCostEV', 
                  label: (
                    <div className="flex items-center gap-2 w-full" style={{ paddingRight: '8px', paddingLeft: '8px' }}>
                      <span>EV Unit Cost (£)</span>
                      <Pencil className="w-3.5 h-3.5 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help flex-shrink-0" />
                    </div>
                  ), 
                  align: 'right' as const 
                },
                { key: 'electricityCost', label: 'Elec. Cost', align: 'right' as const },
                { key: 'totalEVCost', label: 'Total Cost', align: 'right' as const },
            ]}
            data={vehicleSummaryDataWithTotal}
              onExport={() => {
                // Use actual cost values for export (not JSX elements)
                const csv = ['Vehicle Type,Total Converted EVs,Remaining Diesel,EV %,Battery,Unit Cost,Electricity Cost,Total Cost', ...vehicleSummaryDataWithTotal.map(r => {
                  if ((r as any)._isStickyRow) {
                    // For TOTAL row, use the values directly
                    return `${r.vehicleType},${r.totalEV},${r.totalDiesel},${r.utilisation_rate},${r.batteryCapacity},${r.unitCostEV},${r.electricityCost},${r.totalEVCost}`;
                  }
                  const unitCost = evUnitCosts[(r as any)._formattedType] ?? (r as any)._originalUnitCost;
                  const elecCost = r.electricityCost || '-';
                  return `${r.vehicleType},${r.totalEV},${r.totalDiesel},${r.utilisation_rate},${r.batteryCapacity},£${unitCost.toLocaleString()},${elecCost},${r.totalEVCost}`;
                })].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'vehicle_type_summary.csv';
                a.click();
              }}
          />
          </div>
        </div>
        )}
        
        {/* Empty state when no results */}
        {!isLoading && !error && !fleetSummary && electrificationRuns.length === 0 && (
          <div className="text-center py-12 bg-card/30 rounded-lg border border-border">
            <Battery className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">No Electrification Results Available</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Run an electrification optimisation from the backend to see results here.
              Configure your conversion targets above and generate a conversion plan.
            </p>
          </div>
        )}
      </section>

    </div>
    </>
  );
}
